<template>
  <div class="videoSeek" :class="videoSeekClass">
    <div
      class="videoSeek_date"
      :class="{ videoSeek_no_click: !canEditDateTime }"
    >
      <DateTimePicker
        :date="timestampVal"
        :disabled="isPlayback"
        :nudge-top="dateTimeNudgeTop"
        :nudge-right="dateTimeNudgeRight"
        :hide-data="isSeekbarLayoutMode"
        :min-date="minDatePicker"
        :custom-classes="name"
        is-disable-past-time
        @getDate="getDate($event)"
      />
    </div>
    <div v-if="fullscreenMode" class="videoSeek_header-gap" />
    <div
      v-if="!fullscreenMode"
      class="videoSeek_header"
      :class="{ 'videoSeek_header-small': isSeekbarLayoutMode }"
    >
      <div class="videoSeek_btns">
        <v-btn
          v-show="showCapture && isFullLayoutMode && !isPlayback"
          small
          class="videoSeek_btn"
          :elevation="captureMode ? 0 : 1"
          :class="{ 'videoSeek_btn-active': captureMode }"
          @click="!captureMode && $emit('toggleCaptureMode', true)"
        >
          <span class="videoSeek_btn_icon icon icon-camera2" />スナップショット
        </v-btn>
        <v-btn
          v-show="showTrim && isFullLayoutMode && !isPlayback"
          small
          class="videoSeek_btn"
          :elevation="trimMode ? 0 : 1"
          :class="{ 'videoSeek_btn-active': trimMode }"
          @click="!trimMode && $emit('toggleTrimMode', true)"
        >
          <span class="videoSeek_btn_icon icon icon-cut" />ビデオクリップ
        </v-btn>
      </div>
      <div class="videoSeek_right">
        <div v-show="isFullLayoutMode" class="videoSeek_time-range">
          <v-select
            v-model="selectedTimeRange"
            flat
            :items="timeRangeItems"
            attach=""
            :menu-props="{ top: true, offsetY: true }"
            item-text="text"
            item-value="val"
            @change="handleTimeRangeChange"
          />
        </div>
      </div>
    </div>
    <div v-if="isLive && isFullLayoutMode" class="videoSeek_vod-gap" />
    <div v-show="!isLive && isFullLayoutMode" class="videoSeek_vod">
      <div
        :class="{ videoSeek_disappear: !showPlaybackRate }"
        class="videoSeek_vod_speed"
      >
        <v-select
          v-model="playbackRateVal"
          flat
          :items="playbackRates"
          item-text="text"
          item-value="val"
          :menu-props="{
            offsetY: true,
            contentClass: 'videoSeek_vod_speed--vMenu',
          }"
        />
      </div>
      <div class="videoSeek_vod_controls">
        <v-btn
          text
          class="videoSeek_vod_btn videoSeek_vod_btn-backward"
          @click="debounceAddTime(-10000, 'milliseconds', false)"
        >
          <span class="icon icon-backward" />
        </v-btn>
        <v-btn
          text
          class="videoSeek_vod_btn videoSeek_vod_btn-back-ten-frame"
          @click="debounceAddTime(-333, 'milliseconds', true)"
        >
          <back-ten-frame-svg :color="fullscreenMode ? '#FFFFFF' : null" />
        </v-btn>
        <v-btn
          text
          class="videoSeek_vod_btn videoSeek_vod_btn"
          @click="debounceAddTime(-66, 'milliseconds', true)"
        >
          <span class="icon icon-back-frame" />
        </v-btn>
        <v-btn
          text
          class="videoSeek_vod_btn videoSeek_vod_btn-playing"
          @click="playToggle()"
        >
          <span
            class="icon"
            :class="[isPlaying ? 'icon-pause' : 'icon-play']"
          />
        </v-btn>
        <v-btn
          text
          class="videoSeek_vod_btn"
          @click="debounceAddTime(33, 'milliseconds', true)"
        >
          <span class="icon icon-next-frame" />
        </v-btn>
        <v-btn
          text
          class="videoSeek_vod_btn videoSeek_vod_btn-next-ten-frame"
          @click="debounceAddTime(333, 'milliseconds', true)"
        >
          <next-ten-frame-svg :color="fullscreenMode ? '#FFFFFF' : null" />
        </v-btn>
        <v-btn
          text
          class="videoSeek_vod_btn videoSeek_vod_btn-forward"
          @click="debounceAddTime(10000, 'milliseconds', false)"
        >
          <span class="icon icon-forward" />
        </v-btn>
      </div>
      <div class="videoSeek_vod_liveToggle">
        <div
          v-if="!isPlayback"
          class="videoSeek_vod_liveToggle--content"
          @click="showLive()"
        >
          <v-btn x-small rounded depressed class="videoSeek_vod_liveToggle-btn">
            LIVE<span class="icon icon-play" />
          </v-btn>
        </div>
      </div>
    </div>
    <span v-if="isSeekbarLayoutMode" class="videoSeek-timegap" />
    <div v-show="!isDatetimeLayoutMode" class="videoSeek_indicator_container">
      <div class="videoSeek_indicator" />
    </div>
    <div
      :class="{ 'videoSeek_hide--all': isDatetimeLayoutMode }"
      class="videoSeek_bar"
    >
      <timeline
        ref="timeline"
        :items="items"
        :options="options"
        :events="[
          'rangechange',
          'rangechanged',
          'mouseDown',
          'timechange',
          'timechanged',
          'select',
        ]"
        @rangechange="timechange"
        @select="onEventClick(true)"
      />
      <video-seek-trim
        v-if="trimMode"
        ref="trim"
        :init-end-date="timestampVal"
        :start-range="startRange"
        :end-range="endRange"
        :duration-factor="durationFactor"
        :real-duration="realDuration"
        :timeline-width="timelineWidth"
        :is-live="isLive"
        class="videoSeek_tools"
        @onABChange="handleClipABChange"
      />
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import DateTimePicker from '@/components/inputs/DateTimePicker.vue';
import VideoSeekTrim from '@/components/VideoSeekTrim.vue';
import BackTenFrameSvg from './icons/BackTenFrameSVG.vue';
import NextTenFrameSvg from './icons/NextTenFrameSVG.vue';
import debounce from 'lodash/debounce';
import {
  checkHLSEndTimestamp,
  getTrimPoints,
  mergeEvents,
  seekStartPosition,
} from '@/utils/helpers';
import { VideoControllersType } from '@/globals/enums';
import { tracer } from '@/utils/print';
import { isTimeCloseToLive } from '@/utils/video.util';
// const HammerEvent = {
//   1: 'start',
//   2: 'move',
//   4: 'end',
//   8: 'cancel',
// };

export default {
  name: 'VideoSeek',
  components: {
    DateTimePicker,
    VideoSeekTrim,
    BackTenFrameSvg,
    NextTenFrameSvg,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    maxDays: {
      type: Number,
      default: 30,
    },
    live: {
      type: Boolean,
      default: true,
    },
    fullscreenMode: {
      type: Boolean,
      default: false,
    },
    playing: {
      type: Boolean,
      default: false,
    },
    captureMode: {
      type: Boolean,
      default: false,
    },
    trimMode: {
      type: Boolean,
      default: false,
    },
    playbackRate: {
      type: Number,
      default: 1,
    },
    timestamp: {
      type: Number,
      default: dayjs(new Date()).valueOf(),
    },
    isPlayback: {
      type: Boolean,
      default: false,
    },
    showCapture: {
      type: Boolean,
      default: false,
    },
    showTrim: {
      type: Boolean,
      default: false,
    },
    showPlaybackRate: {
      type: Boolean,
      default: true,
    },
    canEditDateTime: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array,
      default: () => [],
    },
    clip: {
      type: Object,
      default: null,
    },
    layoutMode: {
      type: String,
      default: 'full',
    },
    timelineWidth: {
      type: Number,
      default: 64,
    },
    timeRange: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      layoutModeOpen: false,
      startRange: null,
      endRange: null,
      seekingTime: +this.timestamp,
      direction: null,
      playbackRates: [
        { val: 0.5, text: '0.5倍速' },
        { val: 1, text: '1倍速' },
        { val: 2, text: '2倍速' },
      ],
      timeRangeItems: [
        { val: 1, text: '1時間' },
        { val: 3, text: '3時間' },
        { val: 6, text: '6時間' },
        { val: 12, text: '12時間' },
        { val: 24, text: '24時間' },
      ],
      durationFactor: 1, // Timeline duration in hours (default: 1)
      selectedTimeRange: 1,
      addTimeStarted: false,
      groups: [],
      isMouseDown: false,
      isMovingTimeline: false,
      isMovingTimelineTimeout: null,
      options: {
        editable: false,
        zoomMin: dayjs.duration({ hours: 1 }).asMilliseconds(), // one day in milliseconds
        zoomMax: dayjs.duration({ days: 1 }).asMilliseconds(), // about three months in milliseconds
        format: {
          minorLabels: {
            second: 'HH:mm',
            minute: 'HH:mm',
          },
        },
        moveable: this.canEditDateTime,
        zoomable: false,
        selectable: this.canEditDateTime,
      },
      loopMax: null,
      loopNow: null,
      restart: null,
      stop: false,
      timestampVal: 0,
      layoutModeItems: [
        { text: '全表示', value: 'full' },
        { text: 'シークバーのみ表示', value: 'seekbar' },
        { text: '日時のみ表示', value: 'datetime' },
      ],
      realDuration: null,
    };
  },
  computed: {
    minDatePicker() {
      return dayjs().subtract(this.maxDays, 'day').toDate();
    },
    range() {
      return (this.durationFactor * 60) / 2;
    },
    isLive: {
      get() {
        return this.live;
      },
      set(val) {
        this.$emit('update:live', val);
      },
    },
    isPlaying: {
      get() {
        return this.playing;
      },
      set(val) {
        this.$emit('update:playing', val);
      },
    },
    playbackRateVal: {
      get() {
        return this.playbackRate;
      },
      set(val) {
        this.$emit('update:playbackRate', val);
      },
    },
    items() {
      const items = [];
      mergeEvents(this.events).forEach((event, index) => {
        if (event && event.start && event.end) {
          items.push({
            className: 'videoSeek_event',
            end: event.end,
            id: 'event-' + event.eventId + '_' + index,
            start: event.start,
            type: 'background',
          });
        }
      });
      if (this.clip) {
        items.push({
          className: 'videoSeek_event',
          end: this.clip.end,
          id: 'clip',
          start: this.clip.start,
          type: 'background',
        });
      }

      return items;
    },
    isFullLayoutMode() {
      return this.layoutMode === 'full';
    },
    isSeekbarLayoutMode() {
      return this.layoutMode === 'seekbar';
    },
    isDatetimeLayoutMode() {
      return this.layoutMode === 'datetime';
    },
    videoSeekClass() {
      return {
        videoSeek_fullscreen: this.fullscreenMode,
        'videoSeek_display-mode--full': this.isFullLayoutMode,
        'videoSeek_display-mode--seekbar': this.isSeekbarLayoutMode,
        'videoSeek_display-mode--datetime': this.isDatetimeLayoutMode,
      };
    },
    dateTimeNudgeTop() {
      if (this.isFullLayoutMode) {
        return 60;
      } else if (this.isSeekbarLayoutMode) {
        return 35;
      } else {
        return 35;
      }
    },
    dateTimeNudgeRight() {
      if (this.isDatetimeLayoutMode) {
        return -50;
      } else {
        return -100;
      }
    },
  },
  watch: {
    durationFactor: {
      handler: function (val) {
        if (val && this.$refs.timeline) {
          // const timeline = this.$refs.timeline.timeline
          // timeline.setOptions(this.options)
        }
      },
      immediate: true,
    },
    canEditDateTime: {
      handler: function (val) {
        if (this.$refs.timeline) {
          const timeline = this.$refs.timeline.timeline;
          this.options.moveable = val;
          this.options.selectable = val;
          timeline.setOptions(this.options);
          timeline.redraw();
        }
      },
    },
    timestamp: {
      handler: function (val) {
        if (this.isPlaying && this.$refs.timeline && !this.isMovingTimeline) {
          this.handleTimestampChange(val);
        }
      },
    },
    playing: {
      handler: function (val) {
        if (val) {
          this.addTimeStarted = true;
        }
      },
      immediate: true,
    },
    timeRange(val) {
      this.syncTimeRange(val);
    },
  },
  created() {
    this.timechange = this.timechange.bind(this);
    this.debounceAddTime = debounce(this.addTime, 500);
    this.init();
  },
  beforeDestroy() {
    const { timeline } = this.$refs.timeline;
    if (timeline) {
      timeline.off('mousewheel', this.handleMousewheel);
      timeline.off('mouseDown', this.handleMousedown);
      timeline.off('mouseMove', this.handleMousemove);
      timeline.off('release', this.handleRelease);
    }
  },
  mounted() {
    const { timeline } = this.$refs.timeline;
    timeline.on('mousewheel', this.handleMousewheel);
    timeline.on('mouseDown', this.handleMousedown);
    timeline.on('mouseMove', this.handleMousemove);
    timeline.on('release', this.handleRelease);
    this.getMax();

    this.timestampVal = dayjs(this.start).valueOf();
    this.isPlaying = true;
    this.syncTimeRange(this.timeRange);
    // timeline.hammer.on('hammer.input', (event) => {
    //   console.log('event', HammerEvent[event.eventType], event);
    // });
  },
  unmounted() {
    clearInterval(this.loopMax);
    clearInterval(this.loopNow);
    clearInterval(this.restart);
  },
  methods: {
    syncTimeRange(val) {
      if (val == null) {
        return;
      }
      const timeRangeValue = val;
      const hasChanged =
        this.durationFactor !== timeRangeValue ||
        this.selectedTimeRange !== timeRangeValue;
      if (!hasChanged) {
        return;
      }
      this.selectedTimeRange = timeRangeValue;
      this.durationFactor = timeRangeValue;
      this.$nextTick(() => {
        const timeline = this.$refs.timeline?.timeline;
        if (timeline) {
          this.handleTimestampChange(this.timestampVal || this.timestamp);
        }
      });
    },
    setRanges() {
      const { timeline } = this.$refs.timeline;
      this.startRange = timeline.range.start;
      this.endRange = timeline.range.end;
    },
    handleSelectLayoutMode(item) {
      this.$emit('layoutModeChanged', item.value);
    },
    handleRelease: debounce(function () {
      if (this.canEditDateTime) {
        this.seekToDropTime();
      }
      this.isMouseDown = false;
      clearTimeout(this.isMovingTimelineTimeout);
      this.isMovingTimelineTimeout = setTimeout(() => {
        this.isMovingTimeline = false;
      }, 500);
    }, 50), // The vis-timeline component is triggering the mouse release event twice. To handle this, a debounce function is being used to prevent the release event from being processed twice.
    handleMousedown() {
      if (this.canEditDateTime) {
        this.isMouseDown = true;
      }
    },
    handleMousemove() {
      if (this.isMouseDown) {
        this.isMovingTimeline = true;
      }
    },
    handlePlaybackRate() {
      this.$emit('onPlaybackRate', this.playbackRateVal);
    },
    refresh(timestamp) {
      clearInterval(this.restart);
      let playPoint = dayjs(timestamp);
      const start = playPoint
        .subtract(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      const end = playPoint
        .add(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      this.options.start = start;
      this.options.end = end;

      this.options.max = end;
      const livePlay = () => {
        this.changeCurrentTime({
          startTimestamp: dayjs(start).add(this.range, 'minutes').valueOf(),
          live: false,
        });
        clearInterval(this.restart);
      };
      this.restart = setInterval(livePlay, 500);
      this.timestampVal = dayjs(start).add(this.range, 'minutes').valueOf();
    },
    handleClipABChange(ab) {
      const { timeline } = this.$refs.timeline;

      const timePoints = getTrimPoints(
        ab.start,
        ab.end,
        this.durationFactor,
        ab.parentWidth,
        timeline.range.start
      );
      this.realDuration = {
        start: timePoints[0],
        end: timePoints[1],
        movePoint: ab.movePoint,
      };
      this.$emit('onTrimChange', {
        start: timePoints[0],
        end: timePoints[1],
        movePoint: ab.movePoint,
        minRange: ab.minRange,
        durationFactor: this.durationFactor,
      });
    },
    init() {
      let playPoint = dayjs(this.timestamp);
      const start = playPoint
        .subtract(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      const end = playPoint
        .add(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      this.options.start = start;
      this.options.end = end;

      this.options.max = end;
    },
    refreshTimeline() {
      // sometimes timeline UI doesn't display correctly when we change the start/end/max, in this case we use this
      // solution to refresh the timeline
      const currentdurationFactor = this.durationFactor + 0;
      this.durationFactor = currentdurationFactor;
      this.handleTimestampChange(this.timestampVal);
    },
    handleTimeRangeChange(val) {
      this.$emit('onSetTimeRange', val);
      this.selectedTimeRange = val;
      this.durationFactor = val;
      this.handleTimestampChange(this.timestampVal);
      this.captureMode && this.$emit('toggleCaptureMode', false);
      this.trimMode && this.$emit('toggleTrimMode', false);
    },
    handleMousewheel(e) {
      e.preventDefault();
      e.stopPropagation();
      let delta = 0;
      if (event.wheelDelta) {
        /* IE/Opera. */
        delta = event.wheelDelta / 120;
      } else if (event.detail) {
        /* Mozilla case. */
        // In Mozilla, sign of delta is different than in IE.
        // Also, delta is multiple of 3.
        delta = -event.detail / 3;
      } else if (event.deltaY) {
        delta = -event.deltaY / 3;
      }
      this.durationFactor -= delta;
      this.durationFactor =
        this.durationFactor < 1
          ? Math.max(1, this.durationFactor)
          : this.durationFactor > 24
          ? 24
          : this.durationFactor;

      this.selectedTimeRange = this.getRangeByHour(this.durationFactor);
      this.handleTimestampChange(this.timestampVal);
    },
    handleTimestampChange(val) {
      this.timestampVal = val;
      const timeline = this.$refs.timeline.timeline;
      const day = dayjs(val);
      const start = day
        .subtract(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      let end = day.add(this.range, 'minutes').format('YYYY-MM-DDTHH:mm:ss');

      let max = dayjs()
        .add(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      timeline.setOptions({ start });
      timeline.setOptions({ end, max });
      // if (this.name === 'HomeVideo') {
      //   console.log('durationFactor', this.durationFactor);
      // }

      // timeline.setOptions({
      //   timeAxis: {
      //     scale: 'minute',
      //     step: this.durationFactor === 3 ? 25 : 5,
      //   },
      // });

      // update trim selection when seekbar range changes (during drag)
      if (this.trimMode) {
        this.$nextTick(() => {
          this.refreshTrim();
        });
      }
    },
    handleRangeChange({ start, end }) {
      const startDate = dayjs(start);
      const endDate = dayjs(end);
      const duration = Math.ceil(
        dayjs.duration(endDate.diff(startDate)).asHours()
      );
      this.durationFactor = duration;
      this.selectedTimeRange = this.getRangeByHour(duration);
    },
    getRangeByHour(hour) {
      if (hour < 3) {
        return 1;
      } else if (hour < 6) {
        return 3;
      } else if (hour < 12) {
        return 6;
      } else if (hour < 24) {
        return 12;
      } else {
        return 24;
      }
    },
    getMax() {
      const timeline = this.$refs.timeline.timeline;
      if (this.loopMax) {
        clearInterval(this.loopMax);
      }
      this.loopMax = setInterval(() => {
        if (timeline) {
          try {
            this.refreshTimeline();
            this.setRanges();
          } catch (error) {
            // Do nothing
          }
        }
      }, 1000);
    },
    playToggle() {
      this.stop = false;
      this.isPlaying = !this.isPlaying;
    },
    showLive() {
      this.isLive = true;
      this.playbackRateVal = 1;
      this.$emit('onLive');
    },
    // Moving seekbar
    onMovingSeekbarTime(e) {
      const newTimestamp = dayjs(e.start).add(this.range, 'minutes').valueOf();

      this.seekingTime = newTimestamp;
      this.timestampVal = newTimestamp;

      this.direction =
        e.event && e.event.deltaX
          ? e.event.deltaX > 0
            ? 'left'
            : 'right'
          : null;
      // play video when ever seek-bar dragged
      this.isPlaying = true;
    },
    seekToDropTime() {
      if (this.shouldGoLive(this.seekingTime)) {
        return;
      }
      const currentTimePayload = {
        startTimestamp: this.seekingTime,
        live: false,
        pauseAfterLoad: false,
        startPosition: seekStartPosition,
        endTimestamp: checkHLSEndTimestamp(this.seekingTime),
        controller: VideoControllersType.DragSeekbar,
      };
      if (this.direction) {
        currentTimePayload.direction = this.direction;
      }
      this.changeCurrentTime(currentTimePayload);
    },
    refreshTrim() {
      // update trim for new time change
      if (this.$refs.trim) {
        this.$refs.trim.getTimeFromPixel();
      }
    },
    changeCurrentTime(payload) {
      // this.refreshTrim();
      this.$emit('changeCurrentTime', payload);
    },
    timechange(e) {
      if (!this.canEditDateTime) {
        return;
      }
      if (this.stop) {
        this.isPlaying = false;
        return;
      }
      // When change current time on timeline
      if (this.isMouseDown) {
        if (e) {
          this.handleRangeChange(e);
        }

        this.isPlaying = false;

        if (this.clip && this.clip.start && this.clip.end) {
          this.onClip();
          this.isPlaying = true;
        } else {
          this.onMovingSeekbarTime(e);
        }
      }
    },
    getDate(val) {
      // Test timestamps
      // val = 1722232860000; // video off
      // val = 1722233280000; //audio off (frequent)
      // val = 1722233340000; //audio off (frequent)
      // val = 1723795200000; //audio off (single)

      this.isLive = false;
      if (this.shouldGoLive(val)) {
        return;
      }
      this.timestampVal = val;

      let endTimestamp = checkHLSEndTimestamp(this.timestampVal);
      const timePayload = {
        startTimestamp: this.timestampVal,
        endTimestamp: endTimestamp,
        live: false,
        pauseAfterLoad: false,
        startPosition: seekStartPosition,
        controller: VideoControllersType.DragSeekbar,
      };
      this.changeCurrentTime(timePayload);
      this.refreshTimeline();
      this.$emit('onGetDate', val);
    },
    addTime(val, unit, pauseAfterLoad) {
      let newTimestamp = this.timestampVal + 0;
      if (val > 0) {
        newTimestamp = dayjs(this.timestampVal).add(val, unit).valueOf();
      } else {
        newTimestamp = dayjs(this.timestampVal)
          .subtract(val * -1, unit)
          .valueOf();
      }
      if (this.shouldGoLive(newTimestamp)) {
        return;
      }
      tracer('[VideoSeek]/addTime', {
        oldTimestamp: dayjs(this.timestampVal).format('HH:mm:ss'),
        newTimestamp: dayjs(newTimestamp).format('HH:mm:ss'),
        val,
        unit,
        pauseAfterLoad,
      });
      this.timestampVal = newTimestamp;

      if (pauseAfterLoad) {
        this.isPlaying = false;
      }

      this.handleTimestampChange(this.timestampVal);
      this.changeCurrentTime({
        startTimestamp: this.timestampVal,
        live: false,
        pauseAfterLoad,
        value: val,
        unit,
        startPosition: seekStartPosition,
        controller:
          val > 0
            ? VideoControllersType.ForwardSeekbar
            : VideoControllersType.BackwardSeekbar,
      });
    },
    shouldGoLive(timestamp) {
      if (isTimeCloseToLive(timestamp)) {
        this.showLive();
        return true;
      }
      return false;
    },
    onEventClick(play = false) {
      const start = dayjs(this.items[0].start).valueOf();
      if (play) {
        this.stop = false;
      }
      this.changeCurrentTime({
        startTimestamp: start,
        live: false,
      });
    },
    onClip(play = false) {
      const start = dayjs(this.clip.start).valueOf();
      if (play) {
        this.stop = false;
      }
      this.changeCurrentTime({
        startTimestamp: start,
        live: false,
      });
    },
  },
};
</script>

<style lang="scss">
.videoSeek {
  position: relative;
  &-timegap {
    height: 3px;
    display: block;
  }
  &_disappear {
    &,
    & * {
      visibility: hidden;
    }
  }
  &_no_click {
    pointer-events: none;
  }
  &_display-mode--datetime {
    .dateTimePicker {
      &_dateTime {
        display: flex;
        margin-top: 10px;
      }
      &_select {
        font-size: 16px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #0075c2;
        margin-top: 0;
        line-height: 2;
        margin-right: 26px;
      }
      &_time {
        font-size: 16px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #0075c2;
        line-height: 2;
      }
    }
    .videoSeek {
      &_date {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      &_right {
        margin-top: 11px;
      }
    }
    &.videoSeek {
      max-height: 46px;
    }
  }
  &_hide {
    visibility: hidden;
    max-height: 0;
    max-width: 0;
    min-height: 0 !important;
    &--all {
      &,
      & * {
        visibility: hidden;
        max-height: 0;
        max-width: 0;
        min-height: 0 !important;
      }
    }
  }
  &_tools {
    margin-top: 10px;
  }
  &_right {
    display: flex;
    align-items: center;
  }
  &_date {
    display: flex;
    justify-content: center;
    font-size: 18px;
    line-height: 32px;
    font-weight: 600;
    position: absolute;
    width: 100%;
    text-align: center;
    top: 0;
    color: var(--primary-color);
    @media (max-width: 1199px) {
      top: 32px;
    }
    &-fullscreen {
      padding-right: 40px;
    }
  }
  &_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    &-small {
      margin-bottom: -12px;
    }
    @media (max-width: 1199px) {
      margin-bottom: 60px;
    }
  }
  &_header-gap {
    margin-bottom: 28px;
    height: 28px;
    @media (max-width: 1199px) {
      margin-bottom: 60px;
    }
  }
  &_btn {
    margin-right: 10px;

    &_icon {
      margin-right: 6px;
      font-size: 13.3px;
    }
    &-active {
      box-shadow: 0 1px 2px 0 rgb(0 0 0 / 16%) !important;
      background-color: #dbe2f3 !important;
    }
  }
  &_menu {
    border-radius: 10px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.12);
    background-color: #fff;
    width: 182px;
    height: 152px;
    &-icon {
      &.v-btn.v-size--default {
        font-size: 15px;
      }
    }

    &-header {
      padding: 13px 16px 0;
      h4 {
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #666;
      }
      margin-bottom: 7px;
    }
    &-title-active {
      color: #0075c2 !important;
      font-weight: bold !important;
    }
    &-list {
      &-active {
        color: rebeccapurple;
        .v-list-item__icon {
          .icon.icon-check {
            color: #0075c2;
            font-size: 19px;
          }
        }
      }
      &.v-list {
        padding: 0;

        .v-list-item {
          max-height: 32px;
          min-height: 32px;
        }
        .v-list-item__title {
          font-size: 14px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          letter-spacing: normal;
          text-align: left;
          color: #666;
          display: flex;
          align-items: center;
        }
        .v-list-item__icon:first-child {
          margin-right: 0;
        }
        .v-list-item__icon {
          margin: 5px 0;
        }
      }
    }
  }
  &_time {
    font-size: 16px;
    line-height: 24px;
    height: 24px;
    font-weight: 600;
    width: 100%;
    text-align: center;
    color: inherit;
  }
  &_time-range {
    width: 120px;
    .v-text-field {
      padding-top: 0;
      margin-top: 0;
      .v-input__append-inner {
        padding-left: 0;
      }
    }
    .v-input__slot {
      margin-bottom: 0;
    }
    .v-text-field__details {
      display: none;
    }
    .v-select__selection {
      flex-grow: 1;
      text-align: right;
      margin-right: 0;
      max-width: 100%;
    }
    .v-text-field input {
      width: 0;
      flex-grow: 0;
    }
    .v-select__selection.v-select__selection--comma {
      font-size: 12px;
      font-weight: 600;
      color: #989898;
    }
    .v-text-field > .v-input__control > .v-input__slot:before {
      content: none;
    }
    .v-text-field > .v-input__control > .v-input__slot:before {
      border-style: none;
    }
    .v-text-field > .v-input__control > .v-input__slot:after {
      border-style: none;
    }
  }
  &_vod {
    display: flex;
    justify-content: center;
    align-items: center;
    &_controls {
      width: 350px;
      display: flex;
      justify-content: space-around;
      align-items: center;
      margin: 0 15px;
    }
    &-gap {
      height: 35px;
    }
    &_speed {
      width: 120px;
      .v-text-field.v-input--is-focused
        > .v-input__control
        > .v-input__slot:after {
        display: none;
      }
      .v-text-field {
        padding-top: 0;
        margin-top: 0;
        .v-input__append-inner {
          padding-left: 0;
        }
      }
      .v-input__slot {
        margin-bottom: 0;
      }
      .v-text-field__details {
        display: none;
      }
      .v-select__selection {
        flex-grow: 1;
        text-align: right;
        margin-right: 0;
        max-width: 100%;
      }
      .v-text-field input {
        width: 0;
        flex-grow: 0;
      }
      .v-select__selection.v-select__selection--comma {
        font-size: 12px;
        font-weight: 600;
        color: #989898;
      }
      .v-text-field > .v-input__control > .v-input__slot:before {
        content: none;
      }
      &--vMenu {
        margin-top: -30px;
        border-radius: 10px;
        .v-list-item__title {
          gap: 10px;
          display: flex;
          padding-left: 30px !important;
        }
        .v-list-item--active {
          .v-list-item__title {
            color: #0075c2 !important;
            padding-left: 0 !important;
          }
        }
      }
    }
    &_btn {
      &.v-btn:not(.v-btn--round).v-size--default {
        padding: 0;
        min-width: 35px;
        margin: 0 5px;
      }
      .icon {
        color: #989898;
        font-size: 30px;
      }
      &-next-ten-frame {
        position: relative;
        &-label {
          position: absolute;
          top: 6.8px;
          left: 13px;
          font-size: 8px;
          letter-spacing: 0.2px;
          font-weight: 700;
          color: #fff;
        }
      }
      &-back-ten-frame {
        position: relative;
        &-label {
          position: absolute;
          top: 6.8px;
          left: 10px;
          font-size: 8px;
          letter-spacing: 0.2px;
          font-weight: 700;
          color: #fff;
        }
      }
    }
    &_liveToggle {
      height: 21px;
      width: 120px;
      display: flex;
      justify-content: center;
      &-btn {
        &.theme--light.v-btn.v-btn--has-bg {
          background-color: #dbe2f3;
          height: 21px;
          padding: 0 6px;
        }
        .v-btn__content {
          font-size: 14px;
          font-weight: 600;
          line-height: 21px;
        }
      }
      &--content {
        cursor: pointer;
      }
    }
  }
  &_indicator {
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 8px 6.5px 0 6.5px;
    border-color: #999 transparent transparent transparent;
    &_container {
      display: flex;
      justify-content: center;
      margin-top: 5px;
    }
  }
  &_bar {
    border-radius: 5px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
    min-height: 41px;
    position: relative;
  }

  &_event {
    &.vis-item.vis-background {
      background-color: var(--primary-color);
      cursor: pointer;
    }
  }

  .vis-timeline {
    border: 0;
    height: 41px !important;
  }
  .vis-panel {
    height: 19px !important;
  }
  .vis-current-time {
    background-color: #f3f3f3 !important;
    width: 100% !important;
    height: 18px !important;
  }
  .vis-time-axis.vis-background {
    height: 12px !important;
    top: 7px !important;
    z-index: 3 !important;
    pointer-events: none;
  }
  .vis-time-axis .vis-grid.vis-minor,
  .vis-time-axis .vis-grid.vis-major {
    height: 12px !important;
    border-left: 1px solid #999;
    border-right: 1px solid #999;
    top: -18px;
    background-image: linear-gradient(
      90deg,
      transparent 0%,
      transparent calc(20% - 1px),
      #999 calc(20% - 1px),
      #999 20%,
      transparent 20%,
      transparent calc(40% - 1px),
      #999 calc(40% - 1px),
      #999 40%,
      transparent 40%,
      transparent calc(60% - 1px),
      #999 calc(60% - 1px),
      #999 60%,
      transparent 60%,
      transparent calc(80% - 1px),
      #999 calc(80% - 1px),
      #999 80%,
      transparent 80%
    );
    background-size: 100% 8px;
    background-position: bottom;
  }
  .vis-text.vis-minor {
    left: -18px;
    font-size: 11px;
    font-weight: normal;
    color: #999;
  }
  .vis-time-axis.vis-foreground {
    height: 22px;
  }
  .vis-text.vis-major {
    display: none;
  }
  .vis-panel {
    &.vis-center {
      height: 19px !important;
      border-top: 0;
      top: 0px !important;
      pointer-events: all;
      z-index: 1;
    }
    &.vis-top {
      background-color: #c9effc;
    }
  }
  .vis-shadow {
    display: none;
  }
  .vis-item.vis-range {
    top: 0 !important;
    border-width: 0;
    border-radius: 0;
    height: 18px;
    min-width: 15px;
  }
  .vis-panel > .vis-content {
    transform: translateY(0) !important;
  }
  .vis-itemset {
    height: 18px !important;
  }
  .vis-group {
    height: 19px !important;
  }
  .vis-panel.vis-bottom {
    top: 19px !important;
  }
  &_fullscreen {
    .videoSeek {
      &_date {
        padding-right: 40px;
        color: #fff;
      }
      &_vod {
        color: #fff;
        &_btn {
          color: inherit !important;
          .v-btn__content {
            color: inherit;
            .icon {
              color: inherit;
            }
          }
        }
        &_speed {
          color: inherit;
          .v-select {
            color: inherit;
            .v-input__slot {
              color: inherit;
              .v-select__selections {
                color: inherit;
                .v-select__selection--comma {
                  color: inherit;
                }
              }
              .v-input__append-inner {
                .theme--light.v-icon {
                  color: inherit;
                }
              }
            }
          }
        }
        &_liveToggle {
          &-btn {
            background-color: transparent !important;
            border: solid 1px #fff;
            color: #fff !important;
          }
        }
      }
      &_bar {
        box-shadow: none;
      }
      &_indicator {
        border-color: #fff transparent transparent transparent;
      }
    }
    // timeline
    .vis-panel {
      &.vis-center {
        border-top: 1px solid #bfbfbf !important;
        border-right: 1px solid #bfbfbf !important;
        border-left: 1px solid #bfbfbf !important;
      }
      &.vis-top {
        background-color: #8a9da4;
      }
    }
    .vis-text.vis-minor {
      color: #fff;
    }
    .vis-time-axis .vis-grid.vis-minor,
    .vis-time-axis .vis-grid.vis-major {
      border-left: 1px solid #fff;
      border-right: 1px solid #fff;
      background-image: linear-gradient(
        90deg,
        transparent 0%,
        transparent calc(20% - 1px),
        #fff calc(20% - 1px),
        #fff 20%,
        transparent 20%,
        transparent calc(40% - 1px),
        #fff calc(40% - 1px),
        #fff 40%,
        transparent 40%,
        transparent calc(60% - 1px),
        #fff calc(60% - 1px),
        #fff 60%,
        transparent 60%,
        transparent calc(80% - 1px),
        #fff calc(80% - 1px),
        #fff 80%,
        transparent 80%
      );
    }
    .vis-current-time {
      background-color: #4a4a4a !important;
      width: 100% !important;
      height: 18px !important;
    }
    .vis-itemset .vis-background,
    .vis-itemset .vis-foreground {
      border: 1px solid #bfbfbf !important;
      border-radius: 5px;
    }
  }
}
</style>
